import React, { useEffect, useState } from 'react';
import {
    Typography,
    Box,
    Card,
    CardContent,
    TextField,
    Alert,
    Chip,
    CircularProgress,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    IconButton,
    Button,
    Checkbox,
    FormControlLabel,
    Stack,
} from '@mui/material';
import { LocationOn, Inventory, CheckCircle, Warning, Add, Remove, Print } from '@mui/icons-material';
import { DebundleBundleInfo, SourceShelfAllocation } from './types';
import { WarehouseAPI } from '../../services/api';
import { useToast } from '../common/useToast';
import {
    clampDebundleQuantity,
    clampShelfAllocation,
    totalAllocated as sumAllocated,
    remainingToAllocate as computeRemaining,
    isAllocationComplete as computeComplete,
} from '../../utils/debundleAllocation';

interface ShelfInventoryItem {
    shelfCode: string;
    quantity: number;
    skuCode: string;
    inventoryType: string;
}

interface DebundleQuantityAndLocationProps {
    selectedBundle: DebundleBundleInfo;
    quantity: number;
    sourceShelfLocation: string;
    onQuantityChange: (quantity: number, sourceShelfLocation: string, sourceShelfAllocations?: SourceShelfAllocation[]) => void;
}

const DebundleQuantityAndLocation: React.FC<DebundleQuantityAndLocationProps> = ({
    selectedBundle,
    quantity,
    onQuantityChange,
}) => {
    const toast = useToast();
    const [maxDebundle, setMaxDebundle] = useState<number>(0);
    const [isLoadingInventory, setIsLoadingInventory] = useState<boolean>(true);
    const [shelfInventory, setShelfInventory] = useState<ShelfInventoryItem[]>([]);
    const [sourceAllocations, setSourceAllocations] = useState<SourceShelfAllocation[]>([]);

    // Print options state
    const [includeBundleName, setIncludeBundleName] = useState(true);
    const [includePickerName, setIncludePickerName] = useState(true);
    const [pickerName, setPickerName] = useState<string>(() => {
        try {
            const last = window.localStorage.getItem('lastPickerName');
            if (last) return last;
            const stored = window.localStorage.getItem('bundleManagerUser');
            if (stored) return JSON.parse(stored).name || '';
        } catch {
            // ignore parse errors
        }
        return '';
    });

    // Fetch shelf-wise inventory for the bundle SKU
    useEffect(() => {
        const fetchShelfInventory = async () => {
            setIsLoadingInventory(true);
            try {
                const response = await WarehouseAPI.getShelfWiseInventory(selectedBundle.bundleSku, 0, 1000);

                // Filter for GOOD_INVENTORY items that match the bundle SKU
                const filteredInventory = response.data.filter(
                    (item: ShelfInventoryItem) =>
                        item.skuCode.toLowerCase() === selectedBundle.bundleSku.toLowerCase() &&
                        item.inventoryType === 'GOOD_INVENTORY' &&
                        item.quantity > 0
                );

                setShelfInventory(filteredInventory);

                // Calculate total available
                const total = filteredInventory.reduce((sum: number, item: ShelfInventoryItem) => sum + item.quantity, 0);
                setMaxDebundle(total);

                // Initialize source allocations from shelves with inventory
                const initialAllocations: SourceShelfAllocation[] = filteredInventory.map((item: ShelfInventoryItem) => ({
                    shelfCode: item.shelfCode,
                    availableQuantity: item.quantity,
                    allocatedQuantity: 0,
                }));
                setSourceAllocations(initialAllocations);

            } catch (error) {
                console.error('Error fetching shelf inventory:', error);
                setShelfInventory([]);
                setMaxDebundle(0);
            } finally {
                setIsLoadingInventory(false);
            }
        };

        fetchShelfInventory();
    }, [selectedBundle.bundleSku]);

    // Update parent when allocations change
    useEffect(() => {
        const combinedShelfLocation = sourceAllocations
            .filter(a => a.allocatedQuantity > 0)
            .map(a => `${a.shelfCode}:${a.allocatedQuantity}`)
            .join(', ');
        onQuantityChange(quantity, combinedShelfLocation, sourceAllocations);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sourceAllocations]);

    const handleQuantityInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = parseInt(e.target.value) || 1;
        const newQuantity = clampDebundleQuantity(value, maxDebundle);

        // Reset allocations when quantity changes
        const resetAllocations = sourceAllocations.map(a => ({
            ...a,
            allocatedQuantity: 0,
        }));
        setSourceAllocations(resetAllocations);

        const combinedShelfLocation = resetAllocations
            .filter(a => a.allocatedQuantity > 0)
            .map(a => `${a.shelfCode}:${a.allocatedQuantity}`)
            .join(', ');
        onQuantityChange(newQuantity, combinedShelfLocation, resetAllocations);
    };

    const handleShelfAllocationChange = (shelfCode: string, newAllocated: number) => {
        const updatedAllocations = sourceAllocations.map(a => {
            if (a.shelfCode === shelfCode) {
                // Ensure we don't exceed available or go below 0
                const validAllocated = clampShelfAllocation(newAllocated, a.availableQuantity);
                return { ...a, allocatedQuantity: validAllocated };
            }
            return a;
        });
        setSourceAllocations(updatedAllocations);
    };

    const totalAllocated = sumAllocated(sourceAllocations);
    const remainingToAllocate = computeRemaining(quantity, sourceAllocations);
    const isQuantityValid = quantity <= maxDebundle && quantity > 0;
    const isAllocationComplete = computeComplete(quantity, sourceAllocations);

    // Auto-allocate from first available shelf
    const handleAutoAllocate = () => {
        let remaining = quantity;
        const autoAllocated = sourceAllocations.map(a => {
            if (remaining <= 0) return { ...a, allocatedQuantity: 0 };
            const toAllocate = Math.min(remaining, a.availableQuantity);
            remaining -= toAllocate;
            return { ...a, allocatedQuantity: toAllocate };
        });
        setSourceAllocations(autoAllocated);
    };

    // Print pick list: Bundle SKU, Source Shelf and Quantity
    const handlePrintPickList = () => {
        const rows = sourceAllocations
            .filter(a => a.allocatedQuantity > 0)
            .map(a => ({
                sku: selectedBundle.bundleSku,
                shelf: a.shelfCode,
                qty: a.allocatedQuantity,
            }));

        if (rows.length === 0) {
            toast.warning('No source allocations to print.');
            return;
        }

        const titleParts: string[] = [];
        if (includeBundleName) titleParts.push(selectedBundle.name);
        if (includePickerName) {
            const storedName = (() => {
                try {
                    const stored = window.localStorage.getItem('bundleManagerUser');
                    if (stored) return JSON.parse(stored).name || '';
                } catch {
                    return '';
                }
                return '';
            })();
            titleParts.push((pickerName && pickerName.trim()) ? pickerName.trim() : storedName || '');
        }
        const title = `Bundle Pick List${titleParts.length ? ' - ' + titleParts.filter(Boolean).join(' - ') : ''} - ${new Date().toLocaleString()}`;

        const style = `
            <style>
                body { font-family: Arial, Helvetica, sans-serif; padding: 24px; }
                h1 { font-size: 18px; margin-bottom: 8px; }
                table { width: 100%; border-collapse: collapse; margin-top: 12px; }
                th, td { border: 1px solid #333; padding: 8px; text-align: left; }
                th { background: #f2f2f2; }
            </style>
        `;

        const rowsHtml = rows.map(r => `
            <tr>
                <td>${r.sku}</td>
                <td>${r.shelf}</td>
                <td style="text-align: right">${r.qty}</td>
            </tr>
        `).join('\n');

        const metaHtmlParts: string[] = [];
        if (includeBundleName) metaHtmlParts.push(`<div><strong>Bundle:</strong> ${selectedBundle.name}</div>`);
        metaHtmlParts.push(`<div><strong>Bundle SKU:</strong> ${selectedBundle.bundleSku}</div>`);
        metaHtmlParts.push(`<div><strong>Total to Pick:</strong> ${quantity}</div>`);
        if (includePickerName) {
            const storedName = (() => {
                try {
                    const stored = window.localStorage.getItem('bundleManagerUser');
                    if (stored) return JSON.parse(stored).name || '';
                } catch {
                    return '';
                }
                return '';
            })();
            metaHtmlParts.push(`<div><strong>Picker:</strong> ${(pickerName && pickerName.trim()) ? pickerName.trim() : storedName || 'N/A'}</div>`);
        }
        metaHtmlParts.push(`<div><strong>Printed At:</strong> ${new Date().toLocaleString()}</div>`);

        const html = `
            <html>
                <head>
                    <title>${title}</title>
                    ${style}
                </head>
                <body>
                    <h1>${title}</h1>
                    <div style="margin-bottom:12px;">${metaHtmlParts.join('')}</div>
                    <table>
                        <thead>
                            <tr>
                                <th>Bundle SKU</th>
                                <th>Pick from Shelf</th>
                                <th>Quantity</th>
                            </tr>
                        </thead>
                        <tbody>
                            ${rowsHtml}
                        </tbody>
                    </table>
                </body>
            </html>
        `;

        const printWindow = window.open('', '_blank');
        if (!printWindow) {
            toast.warning('Popup blocked. Please allow popups to print the pick list.');
            return;
        }

        printWindow.document.open();
        printWindow.document.write(html);
        printWindow.document.close();
        printWindow.focus();
        setTimeout(() => {
            printWindow.print();
        }, 300);
    };

    return (
        <Box>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Inventory />
                Debundle Quantity & Source Location
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Specify how many bundles to debundle and from which shelf locations.
            </Typography>

            {/* Bundle Info */}
            <Card sx={{ mb: 3 }}>
                <CardContent>
                    <Typography variant="subtitle1" gutterBottom fontWeight="bold">
                        Selected Bundle
                    </Typography>
                    <Typography variant="body1">{selectedBundle.name}</Typography>
                    <Typography variant="body2" color="text.secondary">
                        SKU: {selectedBundle.bundleSku}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        Child SKUs that will be returned:
                    </Typography>
                    <Box sx={{ mt: 1, pl: 2 }}>
                        {selectedBundle.childSKUs.map((child) => (
                            <Typography key={child.skuId} variant="body2">
                                • {child.name} × {child.requiredQuantity * quantity}
                            </Typography>
                        ))}
                    </Box>
                </CardContent>
            </Card>

            {/* Maximum Possible Debundles Card */}
            <Card sx={{ mb: 3, bgcolor: maxDebundle > 0 ? 'success.50' : 'warning.50' }}>
                <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                        <Inventory color={maxDebundle > 0 ? 'success' : 'warning'} />
                        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                            Bundle Stock Availability
                        </Typography>
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                        {isLoadingInventory ? (
                            <CircularProgress size={32} />
                        ) : (
                            <Typography variant="h4" sx={{ fontWeight: 'bold', color: maxDebundle > 0 ? 'success.main' : 'warning.main' }}>
                                {maxDebundle}
                            </Typography>
                        )}
                        <Box>
                            <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                                Maximum Possible Debundles
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Based on current bundle SKU inventory across {shelfInventory.length} shelf{shelfInventory.length !== 1 ? 's' : ''}
                            </Typography>
                        </Box>
                        {!isLoadingInventory && (maxDebundle > 0 ? (
                            <CheckCircle color="success" />
                        ) : (
                            <Warning color="warning" />
                        ))}
                    </Box>

                    {isLoadingInventory ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <CircularProgress size={20} />
                            <Typography variant="body2" color="text.secondary">
                                Checking bundle inventory...
                            </Typography>
                        </Box>
                    ) : maxDebundle === 0 ? (
                        <Alert severity="error">
                            <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                                No bundles available to debundle - zero inventory for this bundle SKU
                            </Typography>
                        </Alert>
                    ) : (
                        <Alert severity="info">
                            <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                                Maximum {maxDebundle} bundle{maxDebundle !== 1 ? 's' : ''} can be debundled based on current inventory
                            </Typography>
                        </Alert>
                    )}
                </CardContent>
            </Card>

            {/* Quantity Input */}
            <Card sx={{ mb: 3 }}>
                <CardContent>
                    <Typography variant="subtitle1" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Inventory />
                        Quantity to Debundle
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                        <TextField
                            type="number"
                            label="Number of Bundles"
                            value={quantity}
                            onChange={handleQuantityInputChange}
                            inputProps={{ min: 1, max: maxDebundle > 0 ? maxDebundle : undefined }}
                            sx={{ maxWidth: 300 }}
                            error={!isLoadingInventory && quantity > maxDebundle && quantity > 0}
                            helperText={
                                !isLoadingInventory && quantity > maxDebundle && quantity > 0
                                    ? `Exceeds maximum: ${maxDebundle} bundles available`
                                    : `How many bundles do you want to break down?${maxDebundle > 0 ? ` (max: ${maxDebundle})` : ''}`
                            }
                        />
                        {maxDebundle > 0 && !isLoadingInventory && (
                            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center', mt: 1 }}>
                                <Chip
                                    label={`Max: ${maxDebundle}`}
                                    color="primary"
                                    variant="outlined"
                                    size="small"
                                    onClick={() => {
                                        const resetAllocations = sourceAllocations.map(a => ({ ...a, allocatedQuantity: 0 }));
                                        setSourceAllocations(resetAllocations);
                                        onQuantityChange(maxDebundle, '', resetAllocations);
                                    }}
                                    sx={{ cursor: 'pointer' }}
                                />
                                {maxDebundle >= 5 && (
                                    <Chip
                                        label="Set 5"
                                        color="secondary"
                                        variant="outlined"
                                        size="small"
                                        onClick={() => {
                                            const resetAllocations = sourceAllocations.map(a => ({ ...a, allocatedQuantity: 0 }));
                                            setSourceAllocations(resetAllocations);
                                            onQuantityChange(Math.min(5, maxDebundle), '', resetAllocations);
                                        }}
                                        sx={{ cursor: 'pointer' }}
                                    />
                                )}
                                {maxDebundle >= 10 && (
                                    <Chip
                                        label="Set 10"
                                        color="secondary"
                                        variant="outlined"
                                        size="small"
                                        onClick={() => {
                                            const resetAllocations = sourceAllocations.map(a => ({ ...a, allocatedQuantity: 0 }));
                                            setSourceAllocations(resetAllocations);
                                            onQuantityChange(Math.min(10, maxDebundle), '', resetAllocations);
                                        }}
                                        sx={{ cursor: 'pointer' }}
                                    />
                                )}
                            </Box>
                        )}
                    </Box>

                    {!isLoadingInventory && !isQuantityValid && quantity > 0 && (
                        <Alert severity="warning" sx={{ mt: 2 }}>
                            <Typography variant="body2">
                                ⚠️ Requested quantity ({quantity}) exceeds available bundle stock ({maxDebundle})
                            </Typography>
                        </Alert>
                    )}
                </CardContent>
            </Card>

            {/* Source Shelf Allocation */}
            {!isLoadingInventory && shelfInventory.length > 0 && (
                <Card sx={{ mb: 3 }}>
                    <CardContent>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                            <Box>
                                <Typography variant="subtitle1" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0 }}>
                                    <LocationOn />
                                    Source Shelf Locations
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Select quantity to take from each shelf location
                                </Typography>
                            </Box>
                            <Chip
                                label="Auto-Allocate"
                                color="primary"
                                onClick={handleAutoAllocate}
                                sx={{ cursor: 'pointer' }}
                            />
                        </Box>

                        {/* Allocation Status */}
                        <Alert
                            severity={isAllocationComplete ? 'success' : remainingToAllocate > 0 ? 'info' : 'warning'}
                            sx={{ mb: 2 }}
                        >
                            {isAllocationComplete ? (
                                <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                                    ✓ All {quantity} bundles allocated from source shelves
                                </Typography>
                            ) : remainingToAllocate > 0 ? (
                                <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                                    Allocated: {totalAllocated} / {quantity} — Need to allocate {remainingToAllocate} more
                                </Typography>
                            ) : (
                                <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                                    Over-allocated by {Math.abs(remainingToAllocate)}. Please reduce allocations.
                                </Typography>
                            )}
                        </Alert>

                        <TableContainer component={Paper} sx={{ bgcolor: 'background.paper' }}>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell><strong>Shelf Location</strong></TableCell>
                                        <TableCell align="center"><strong>Available</strong></TableCell>
                                        <TableCell align="center"><strong>Take Quantity</strong></TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {sourceAllocations.map((allocation) => (
                                        <TableRow key={allocation.shelfCode}>
                                            <TableCell>
                                                <Chip
                                                    label={allocation.shelfCode}
                                                    size="small"
                                                    color={allocation.allocatedQuantity > 0 ? 'primary' : 'default'}
                                                />
                                            </TableCell>
                                            <TableCell align="center">
                                                <Typography variant="body2" color="text.secondary">
                                                    {allocation.availableQuantity}
                                                </Typography>
                                            </TableCell>
                                            <TableCell align="center">
                                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => handleShelfAllocationChange(allocation.shelfCode, allocation.allocatedQuantity - 1)}
                                                        disabled={allocation.allocatedQuantity <= 0}
                                                    >
                                                        <Remove fontSize="small" />
                                                    </IconButton>
                                                    <TextField
                                                        type="number"
                                                        value={allocation.allocatedQuantity}
                                                        onChange={(e) => handleShelfAllocationChange(allocation.shelfCode, parseInt(e.target.value) || 0)}
                                                        inputProps={{
                                                            min: 0,
                                                            max: allocation.availableQuantity,
                                                            style: { textAlign: 'center', width: 60 }
                                                        }}
                                                        size="small"
                                                        sx={{ width: 80 }}
                                                    />
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => handleShelfAllocationChange(allocation.shelfCode, allocation.allocatedQuantity + 1)}
                                                        disabled={allocation.allocatedQuantity >= allocation.availableQuantity}
                                                    >
                                                        <Add fontSize="small" />
                                                    </IconButton>
                                                    {allocation.availableQuantity > 0 && (
                                                        <Chip
                                                            label="All"
                                                            size="small"
                                                            variant="outlined"
                                                            onClick={() => handleShelfAllocationChange(allocation.shelfCode, Math.min(allocation.availableQuantity, remainingToAllocate + allocation.allocatedQuantity))}
                                                            sx={{ cursor: 'pointer', ml: 1 }}
                                                        />
                                                    )}
                                                </Box>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>

                        {/* Print Pick List Section - Show when allocations are complete */}
                        {isAllocationComplete && (
                            <Box sx={{ mt: 3, p: 2, bgcolor: 'rgba(30, 41, 59, 0.8)', borderRadius: 1, border: '1px solid rgba(255,255,255,0.1)' }}>
                                <Typography variant="subtitle1" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'white', fontWeight: 'bold' }}>
                                    <Print sx={{ color: 'white' }} />
                                    Print Pick List for Picker
                                </Typography>
                                <Typography variant="body2" sx={{ mb: 2, color: 'white' }}>
                                    Print a list showing which shelves to pick bundles from.
                                </Typography>

                                <Stack direction="row" spacing={2} alignItems="center">
                                    <Button
                                        variant="contained"
                                        size="medium"
                                        startIcon={<Print />}
                                        onClick={handlePrintPickList}
                                        sx={{
                                            backgroundColor: 'primary.main',
                                            color: 'white',
                                            '&:hover': {
                                                backgroundColor: 'primary.dark',
                                            }
                                        }}
                                    >
                                        Print Pick List
                                    </Button>
                                </Stack>

                                <Stack direction="row" spacing={2} sx={{ mt: 2, alignItems: 'center' }}>
                                    <FormControlLabel
                                        control={<Checkbox checked={includeBundleName} onChange={(e) => setIncludeBundleName(e.target.checked)} sx={{ color: 'rgba(255,255,255,0.7)', '&.Mui-checked': { color: 'primary.main' } }} />}
                                        label={<Typography sx={{ color: 'rgba(255,255,255,0.9)' }}>Include bundle name</Typography>}
                                    />
                                    <FormControlLabel
                                        control={<Checkbox checked={includePickerName} onChange={(e) => setIncludePickerName(e.target.checked)} sx={{ color: 'rgba(255,255,255,0.7)', '&.Mui-checked': { color: 'primary.main' } }} />}
                                        label={<Typography sx={{ color: 'rgba(255,255,255,0.9)' }}>Include picker name</Typography>}
                                    />
                                    {includePickerName && (
                                        <TextField
                                            size="small"
                                            label="Picker name"
                                            value={pickerName}
                                            onChange={(e) => {
                                                setPickerName(e.target.value);
                                                try { window.localStorage.setItem('lastPickerName', e.target.value); } catch { };
                                            }}
                                            sx={{
                                                minWidth: 200,
                                                '& .MuiInputBase-root': {
                                                    bgcolor: 'rgba(255,255,255,0.1)',
                                                    color: 'white',
                                                },
                                                '& .MuiInputLabel-root': {
                                                    color: 'rgba(255,255,255,0.7)',
                                                    '&.Mui-focused': { color: 'primary.main' },
                                                },
                                                '& .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: 'rgba(255,255,255,0.3)',
                                                },
                                                '&:hover .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: 'rgba(255,255,255,0.5)',
                                                },
                                            }}
                                        />
                                    )}
                                </Stack>
                            </Box>
                        )}
                    </CardContent>
                </Card>
            )}

            {!isLoadingInventory && shelfInventory.length === 0 && maxDebundle === 0 && (
                <Alert severity="warning">
                    No shelves found with inventory for this bundle SKU.
                </Alert>
            )}
        </Box>
    );
};

export default DebundleQuantityAndLocation;
